import { useCallback, useMemo, useState } from 'react';
import { FormikValues } from 'formik';
import { isEqual } from 'lodash';
import get from 'lodash/get';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import * as ContentEditor from '@/components/ContentEditor';
import DiscardChangesDialog from '@/components/DiscardChangesDialog';
import useDefaultPublishConfig from '@/components/EditorBuilder/Settings/Dialogs/PublishChanges/useDefaultPublishConfig';
import { onPublishType } from '@/components/EditorBuilder/Settings/types';
import useNavigation from '@/hooks/useNavigation';
import {
  LanguageEnum,
  PublishContentConfig,
  ResourceStatusEnum,
  ResourceTypeEnum,
} from '@/types/schema';

import { FormProps } from '../Form/types';
import { HeaderProps } from '../Header/types';

interface ContentEntityEditorProps<TFormValues extends FormikValues = FormikValues> {
  titlePropName?: string;
  header: Omit<HeaderProps, 'onSaveDraft' | 'shouldPublish' | 'onPublish'>;
  form: FormProps<TFormValues>;
  isDirty: boolean;
  onBack?: () => void;
  onSaveDraft?: (values: TFormValues) => void;
  onSaveDraftAndPreview?: (values: TFormValues) => void;
  onPublish?: (values: TFormValues, publishConfig: PublishContentConfig) => void;
}

function ContentEntityEditor<TFormValues extends FormikValues = FormikValues>({
  titlePropName = 'name',
  header,
  form,
  isDirty,
  onBack: onBackOverride,
  onPublish,
  onSaveDraft,
  onSaveDraftAndPreview,
}: ContentEntityEditorProps<TFormValues>) {
  const { title, id, ...headerProps } = header;
  const { onChange, onSubmit, ...formProps } = form;
  // add type
  const [formikSubmit, setFormikSubmit] = useState();
  const [formValues, setFormValues] = useState<TFormValues | undefined>();
  const [defaultLanguageType, setDefaultLanguageType] = useState<LanguageEnum>(
    formProps.initialValues?.defaultLanguageType ?? LanguageEnum.English,
  );
  const [isPublishPopoverVisible, { off: hidePublishPopover, on: showPublishPopover }] =
    useToggle();
  const [showDiscardChangesDialog, { toggle: toggleDiscardChangesDialog }] = useToggle();
  const navigation = useNavigation();
  const { status, contentType } = headerProps;

  const isMinorVersionPublishAllowed =
    formValues && isEqual(formValues.sections, form.initialValues.sections);

  const defaultPublishConfig = useDefaultPublishConfig(isMinorVersionPublishAllowed);

  // add type
  const _onChange = useCallback(
    (formik: any) => {
      setFormValues(formik.values);
      onChange?.(formik);
    },
    [onChange],
  );

  const _onSaveDraft = useEvent(() => {
    const data: any =
      contentType == ResourceTypeEnum.Path
        ? { ...formValues, defaultLanguageType }
        : { ...formValues };
    formValues && onSaveDraft?.(data);
  });

  const _onSaveDraftAndPreview = useEvent(() => {
    formValues && onSaveDraftAndPreview?.(formValues);
  });

  const _onSubmit = useCallback(
    (values: TFormValues) => {
      if (!status || status === ResourceStatusEnum.Draft) {
        const data =
          contentType == ResourceTypeEnum.Path ? { ...values, defaultLanguageType } : { ...values };
        onPublish?.(data, defaultPublishConfig);
      } else {
        setFormValues(values);
        showPublishPopover();
      }
    },
    [status, onPublish, defaultPublishConfig, showPublishPopover, defaultLanguageType, contentType],
  );

  const _onPublish: onPublishType = useEvent((publishConfig: PublishContentConfig) => {
    formValues && onPublish?.(formValues, publishConfig);
    return new Promise(() => {});
  });

  const goBack = useCallback(() => {
    if (onBackOverride) {
      onBackOverride();
    } else {
      navigation.back();
    }
  }, [onBackOverride, navigation]);

  const onBack = useCallback(() => {
    if (isDirty) {
      toggleDiscardChangesDialog();
    } else {
      goBack();
    }
  }, [isDirty, goBack, toggleDiscardChangesDialog]);

  const publishProps = useMemo(
    () => ({
      isPublishPopoverVisible,
      onPublish: _onPublish,
      hidePublishPopover,
      shouldPublish: !!onPublish,
    }),
    [_onPublish, hidePublishPopover, isPublishPopoverVisible, onPublish],
  );

  return (
    <ContentEditor.Main>
      <ContentEditor.Header
        allowMinorVersionPublish={isMinorVersionPublishAllowed}
        defaultLanguageType={defaultLanguageType}
        id={id}
        publishProps={publishProps}
        setDefaultLanguageType={setDefaultLanguageType}
        title={get(formValues, titlePropName) || title}
        onBack={onBack}
        onSaveDraft={onSaveDraft ? _onSaveDraft : undefined}
        onSaveDraftAndPreview={onSaveDraftAndPreview ? _onSaveDraftAndPreview : undefined}
        onSubmit={formikSubmit}
        {...headerProps}
      />
      <ContentEditor.Form<TFormValues>
        setSubmitRef={setFormikSubmit}
        onChange={_onChange}
        onSubmit={onPublish ? _onSubmit : onSubmit}
        {...formProps}
      />
      {showDiscardChangesDialog ? (
        <DiscardChangesDialog onClose={toggleDiscardChangesDialog} onDiscard={onBackOverride} />
      ) : null}
    </ContentEditor.Main>
  );
}

export default ContentEntityEditor;
